import { useEffect, useState, type FormEvent } from "react";
import type {
  Promotion,
  PromotionInput,
  PromotionBenefitPolicy,
  PromotionPlacements,
  PromotionStatus,
  PromotionStep,
  PromotionTier
} from "./types";

const defaultPlacements: PromotionPlacements = {
  announcementBar: true,
  homeHero: false,
  productCardBadge: true,
  productDetail: true,
  cartNudge: true,
  checkoutSummary: true
};

const emptyPromotion: PromotionInput = {
  id: "",
  name: "",
  status: "draft",
  priority: 0,
  benefitPolicy: "best_benefit",
  startsAt: null,
  endsAt: null,
  rule: {
    allocation: "highest_price_first",
    tiers: [
      {
        minQuantity: 1,
        maxQuantity: null,
        steps: [
          { position: 1, payPct: 100 },
          { position: 2, payPct: 70 },
          { fromPosition: 3, payPct: 50 }
        ]
      }
    ]
  },
  presentation: {
    headline: "Más llevas, menos pagas",
    shortLabel: "Descuento por cantidad",
    badge: "Hasta 50% menos",
    cartNudge: "Agrega {remainingUnits} más para desbloquear el siguiente beneficio.",
    activeMessage: "Promoción aplicada. Estás ahorrando {savings}.",
    terms: "Aplica sobre las referencias participantes.",
    placements: defaultPlacements
  }
};

function stepsToText(steps: PromotionStep[]) {
  return steps
    .map((step) => (
      "position" in step
        ? `${step.position}:${step.payPct}`
        : `${step.fromPosition}+:${step.payPct}`
    ))
    .join(", ");
}

function parseSteps(value: string): PromotionStep[] {
  return value
    .split(",")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = /^(\d+)(\+)?\s*:\s*(\d+)$/.exec(line);
      if (!match) throw new Error(`Paso inválido: "${line}". Usa posición:porcentaje, por ejemplo 2:70 o 3+:50.`);

      const position = Number(match[1]);
      const payPct = Number(match[3]);
      if (position < 1 || payPct < 0 || payPct > 100) {
        throw new Error(`Paso fuera de rango: "${line}".`);
      }

      return match[2]
        ? { fromPosition: position, payPct }
        : { position, payPct };
    });
}

function tiersToText(tiers: PromotionTier[]) {
  return tiers.map((tier) => {
    const range = tier.maxQuantity === null
      ? `${tier.minQuantity}+`
      : tier.maxQuantity === tier.minQuantity
        ? `${tier.minQuantity}`
        : `${tier.minQuantity}-${tier.maxQuantity}`;
    return `${range} => ${stepsToText(tier.steps)}`;
  }).join("\n");
}

function parseTiers(value: string): PromotionTier[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = /^(\d+)(?:-(\d+)|(\+))?\s*=>\s*(.+)$/.exec(line);
      if (!match) {
        throw new Error(`Nivel inválido: "${line}". Usa por ejemplo 2 => 1:100, 2:50 o 3+ => 1:100, 2+:40.`);
      }

      const minQuantity = Number(match[1]);
      const maxQuantity = match[3]
        ? null
        : match[2]
          ? Number(match[2])
          : minQuantity;

      return {
        minQuantity,
        maxQuantity,
        steps: parseSteps(match[4])
      };
    });
}

function toLocalDateTime(value: string | null) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function toIsoOrNull(value: string) {
  return value ? new Date(value).toISOString() : null;
}

export function PromotionEditor({
  promotion,
  onSave,
  onCancel
}: {
  promotion: Promotion | null;
  onSave: (input: PromotionInput, isNew: boolean) => Promise<void>;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<PromotionInput>(emptyPromotion);
  const [tiersText, setTiersText] = useState(tiersToText(emptyPromotion.rule.tiers));
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const next = promotion
      ? {
          id: promotion.id,
          name: promotion.name,
          status: promotion.status,
          priority: promotion.priority,
          benefitPolicy: promotion.benefitPolicy || "best_benefit",
          startsAt: promotion.startsAt,
          endsAt: promotion.endsAt,
          rule: promotion.rule,
          presentation: promotion.presentation
        }
      : emptyPromotion;
    setDraft(next);
    setTiersText(tiersToText(next.rule.tiers));
    setError(null);
  }, [promotion]);

  function updatePresentation(field: keyof PromotionInput["presentation"], value: string) {
    setDraft((current) => ({
      ...current,
      presentation: { ...current.presentation, [field]: value }
    }));
  }

  function togglePlacement(field: keyof PromotionPlacements) {
    setDraft((current) => ({
      ...current,
      presentation: {
        ...current.presentation,
        placements: {
          ...current.presentation.placements,
          [field]: !current.presentation.placements[field]
        }
      }
    }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      const input = {
        ...draft,
        startsAt: draft.startsAt ? toIsoOrNull(draft.startsAt) : null,
        endsAt: draft.endsAt ? toIsoOrNull(draft.endsAt) : null,
        rule: {
          allocation: "highest_price_first" as const,
          tiers: parseTiers(tiersText)
        }
      };
      await onSave(input, !promotion);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "No se pudo guardar la promoción.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form className="promotion-editor" onSubmit={submit}>
      <div className="promotion-editor-heading">
        <div>
          <span>Configuración comercial</span>
          <h2>{promotion ? `Editar ${promotion.name}` : "Nueva promoción"}</h2>
        </div>
        <button className="ghost-button" type="button" onClick={onCancel}>Cerrar</button>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      <div className="form-grid">
        <label>
          ID estable
          <input
            value={draft.id}
            disabled={Boolean(promotion)}
            placeholder="volumen-general"
            pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
            required
            onChange={(event) => setDraft({ ...draft, id: event.target.value })}
          />
        </label>
        <label>
          Nombre interno
          <input value={draft.name} required onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
        </label>
        <label>
          Estado
          <select
            value={draft.status}
            onChange={(event) => setDraft({ ...draft, status: event.target.value as PromotionStatus })}
          >
            <option value="draft">Borrador</option>
            <option value="active">Activa</option>
            <option value="inactive">Inactiva</option>
            <option value="archived">Archivada</option>
          </select>
        </label>
        <label>
          Prioridad
          <input
            type="number"
            value={draft.priority}
            onChange={(event) => setDraft({ ...draft, priority: Number(event.target.value) })}
          />
        </label>
        <label>
          Politica embajador
          <select
            value={draft.benefitPolicy}
            onChange={(event) => setDraft({ ...draft, benefitPolicy: event.target.value as PromotionBenefitPolicy })}
          >
            <option value="best_benefit">Mejor beneficio</option>
            <option value="brand_exclusive">Exclusiva de marca</option>
          </select>
          <small>En exclusiva de marca no compite el descuento del embajador.</small>
        </label>
        <label>
          Inicia
          <input
            type="datetime-local"
            value={toLocalDateTime(draft.startsAt)}
            onChange={(event) => setDraft({ ...draft, startsAt: event.target.value || null })}
          />
        </label>
        <label>
          Finaliza
          <input
            type="datetime-local"
            value={toLocalDateTime(draft.endsAt)}
            onChange={(event) => setDraft({ ...draft, endsAt: event.target.value || null })}
          />
          <small>Vacío significa sin fecha de finalización.</small>
        </label>
      </div>

      <label>
        Niveles por cantidad total y porcentaje pagado por posición
        <textarea
          rows={5}
          value={tiersText}
          onChange={(event) => setTiersText(event.target.value)}
          placeholder={"1+ => 1:100, 2:70, 3+:50"}
          required
        />
        <small>
          Una línea por nivel. Ejemplos: “2 =&gt; 1:100, 2:50” y
          “3+ =&gt; 1:100, 2+:40”. Los niveles no pueden superponerse.
        </small>
      </label>

      <div className="form-grid">
        <label>
          Titular
          <input value={draft.presentation.headline} required onChange={(event) => updatePresentation("headline", event.target.value)} />
        </label>
        <label>
          Etiqueta corta
          <input value={draft.presentation.shortLabel} required onChange={(event) => updatePresentation("shortLabel", event.target.value)} />
        </label>
        <label>
          Badge
          <input value={draft.presentation.badge} required onChange={(event) => updatePresentation("badge", event.target.value)} />
        </label>
        <label>
          Mensaje para completar
          <input value={draft.presentation.cartNudge} onChange={(event) => updatePresentation("cartNudge", event.target.value)} />
        </label>
        <label>
          Mensaje aplicado
          <input value={draft.presentation.activeMessage} onChange={(event) => updatePresentation("activeMessage", event.target.value)} />
        </label>
        <label>
          Términos
          <textarea rows={3} value={draft.presentation.terms} onChange={(event) => updatePresentation("terms", event.target.value)} />
        </label>
      </div>

      <fieldset>
        <legend>Ubicaciones visibles</legend>
        <div className="placement-grid">
          {Object.entries(draft.presentation.placements).map(([field, enabled]) => (
            <label key={field} className="check-label">
              <input
                type="checkbox"
                checked={enabled}
                onChange={() => togglePlacement(field as keyof PromotionPlacements)}
              />
              {field}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="button-row">
        <button className="primary-button" type="submit" disabled={isSaving}>
          {isSaving ? "Guardando..." : "Guardar promoción"}
        </button>
        <button className="ghost-button" type="button" onClick={onCancel}>Cancelar</button>
      </div>
    </form>
  );
}
